import cn from 'classnames';
import styles from 'widget/event_components/ticket-spot.scss';
import { TicketImages } from 'widget/event_components/Ticket/components/Images';
import { List } from 'immutable';
import { Col, Button } from 'react-bootstrap';
import { PaymentPriceWrapper } from 'widget/event_components/Ticket/components/PaymentPriceWrapper';
import { CircularProgress } from '@mui/material';
import * as E from './elements';
import { useState, useCallback } from 'react';
import { waitlistTicketSoldout } from '../../waitlist_utils';
import { trackTicketSelection } from '../../../../../utils/journey-tracking';

export const CartTicket = ({
  event,
  ticket,
  cart,
  settings,
  onCartUpdate,
  promoCode,
  loadingCheckoutValues,
  isMobile,
  errors,
  handleTicketOverride,
  handleWaitlistTicketClick,
}) => {
  const id = ticket.get('id');
  const maxPerOrder = ticket.get('maximum_per_order');
  const minPerOrder = ticket.get('minimum_per_order');
  const remainingTickets = ticket.get('available_quantity');

  const maxBasedOnTypeAndCapacity = Math.min(maxPerOrder, remainingTickets);

  const DISABLED_TICKET_STATES = ['soldout', 'cancelled', 'postponed', 'sale-ended', 'sale-not-started'];
  const unavailableTicket = DISABLED_TICKET_STATES.includes(ticket.get('status'));
  const disabledTickets = ticket.get('quantity_total') < ticket.get('quantity_sold') || unavailableTicket;
  const TICKET_STATUS_TEXT = {
    onsale: settings.get('onSaleText'),
    'sale-ended': settings.get('salesEndedText'),
    'sale-not-started': settings.get('salesNotStartedText'),
    soldout: settings.get('soldOutText'),
    cancelled: settings.get('cancelledText'),
    postponed: settings.get('postponedText'),
  };

  const [quantity, setQuantity] = useState(cart[id] || 0);
  const [cartError, setCartError] = useState('');

  const handleCartUpdate = useCallback(
    async (ticketId, qty, promo) => {
      try {
        setCartError('');
        await onCartUpdate(ticketId, qty, promo);
      } catch (error) {
        setCartError(error?.response?.data?.error || 'Failed to update cart. Please try again.');
        throw error;
      }
    },
    [onCartUpdate]
  );

  const handleReducingButtonClick = useCallback(async () => {
    if (quantity < 0) return;

    if (minPerOrder >= quantity) {
      try {
        await handleCartUpdate(id, 0, promoCode);
        setQuantity(0);
      } catch (error) {
        // Error handled in handleCartUpdate
      }
      return;
    }

    const newQuantity = quantity - 1;

    if (window.gtag) {
      window.gtag('event', 'deselect_ticket', {
        event_category: 'Ticket',
        event_label: ticket.get('name'),
        value: newQuantity,
      });
    }
    if (window.fbq) {
      window.fbq('track', 'DeSelectTicket', {
        content_name: ticket.get('name'),
        content_type: 'ticket',
        quantity: newQuantity,
      });
    }

    try {
      await handleCartUpdate(id, newQuantity, promoCode);
      setQuantity(newQuantity);
    } catch (error) {
      // Error handled in handleCartUpdate
    }
  }, [quantity]);

  const handleAdditionButtonClick = useCallback(async () => {
    if (quantity >= maxBasedOnTypeAndCapacity) return;

    const newQuantity = quantity === 0 ? minPerOrder : quantity + 1;

    // Track ticket quantity change
    if (window.gtag) {
      window.gtag('event', 'select_ticket', {
        event_category: 'Ticket',
        event_label: ticket.get('name'),
        value: newQuantity,
      });
    }
    if (window.fbq) {
      window.fbq('track', 'SelectTicket', {
        content_name: ticket.get('name'),
        content_type: 'ticket',
        quantity: newQuantity,
      });
    }

    // Journey tracking
    trackTicketSelection(id, event, newQuantity);

    if (quantity === 0) {
      try {
        await handleCartUpdate(id, minPerOrder, promoCode);
        setQuantity(minPerOrder);
      } catch (error) {
        // Error handled in handleCartUpdate
      }
      return;
    }

    try {
      await handleCartUpdate(id, quantity + 1, promoCode);
      setQuantity(quantity + 1);
    } catch (error) {
      // Error handled in handleCartUpdate
    }
  }, [quantity, ticket, event, id]);

  const handleOnChangeInput = useCallback((changeEvent) => {
    const _value = Number(changeEvent.target.value);
    if (typeof _value !== 'number' || Number.isNaN(_value)) {
      setQuantity(minPerOrder);
      return;
    }

    if (_value < 0) {
      setQuantity(0);
      return;
    }

    if (_value < minPerOrder) {
      setQuantity(minPerOrder);
      return;
    }

    if (_value > maxPerOrder) {
      setQuantity(maxPerOrder);
      return;
    }

    setQuantity(_value);
  }, []);

  const handleOnFocusInput = useCallback(async (changeEvent) => {
    const _value = Number(changeEvent.target.value);
    if (typeof _value !== 'number' || Number.isNaN(_value)) {
      setQuantity(0);
      return;
    }
    try {
      await handleCartUpdate(id, _value, promoCode);
      setQuantity(_value);
    } catch (error) {
      // Error handled in handleCartUpdate, revert to original quantity
    }
  }, []);

  return (
    <div className={cn(styles.ticketWrapper, { [styles.mobileTicketWrapper]: isMobile })}>
      {isMobile && (
        <div className={styles.mobileTicketImages}>
          <TicketImages ticket_images={(ticket.get('image_urls') || List()).toJS()} />
        </div>
      )}
      <Col className={cn(styles.ticket, 'ticket-background')}>
        {!isMobile && (
          <span className={styles.ticketImages}>
            <TicketImages ticket_images={(ticket.get('image_urls') || List()).toJS()} />
          </span>
        )}
        <p className={cn(styles.ticketName, 'ticket-name')}>{ticket.get('name')}</p>
        {ticket.get('description') && <p className={cn(styles.ticketDescription, 'ticket-description')}>{ticket.get('description')}</p>}
        <div className={styles.ticketDivider} />
        <div className={cn(styles.priceQuantity, 'ticket-pricing')}>
          <div className={styles.ticketPrice}>
            {disabledTickets ? (
              <>
                <span className="ticket-sold-out">{TICKET_STATUS_TEXT[ticket.get('status')]}</span>
              </>
            ) : (
              <PaymentPriceWrapper errors={errors} ticket={ticket} event={event} settings={settings} handleTicketOverride={handleTicketOverride} />
            )}
          </div>
          <div className={styles.quantitySelector}>
            <>
              {waitlistTicketSoldout(event, ticket) && (
                <Button
                  onClick={() => {
                    handleCartUpdate(id, 1);
                    handleWaitlistTicketClick();
                  }}
                  className="attendee_button"
                >
                  {event.get('waitlist_call_to_action')}
                </Button>
              )}
              {!unavailableTicket && (
                <div className={`quantity-selector ${styles.ticketQuantity}`}>
                  <Button
                    disabled={quantity <= 0}
                    onClick={handleReducingButtonClick}
                    className={`quantity-selector-button ${styles.ticketQuantityChangeButton}`}
                  >
                    &minus;
                  </Button>
                  {loadingCheckoutValues ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <E.StyledNumberInput
                      value={quantity}
                      onBlur={handleOnFocusInput}
                      onChange={handleOnChangeInput}
                      className="quantity-selector-input"
                    />
                  )}
                  <Button
                    disabled={quantity >= maxBasedOnTypeAndCapacity}
                    onClick={handleAdditionButtonClick}
                    className={`quantity-selector-button ${styles.ticketQuantityChangeButton}`}
                  >
                    &#x2B;
                  </Button>
                </div>
              )}
              {cartError && (
                <div className="error" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                  {cartError}
                </div>
              )}
              {settings.get('showTicketAvailability') && (
                <div className={cn(styles.ticketsAvailability, 'tickets-availability')}>
                  {ticket.get('quantity_total') - ticket.get('quantity_sold')} {settings.get('ticketAvailabilityText')}
                </div>
              )}
            </>
          </div>
        </div>
      </Col>
      <hr />
    </div>
  );
};
